import { useState, useMemo, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  MoreHorizontal, Plus, Minus,
  ChevronLeft, ChevronRight, Clock, Check, Loader2, Target, TrendingUp, Activity,
  Users,
} from 'lucide-react';
import PhaseIntroCard from '@/features/dashboard/PhaseIntroCard';
import { format, addDays } from 'date-fns';
import { useAuth } from '@/features/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWeeklyOne, type WeeklyCommitment } from '@/features/weekly-one/WeeklyOneContext';
import { supabase } from '@/lib/supabase/client';
import {
  useHabitTracker, getWeekEnd, formatMinutes, formatHours, formatCurrency, buildDynamicTitle, type Habit,
} from '@/features/weekly-tracker/useHabitTracker';
import {
  SettingsPanel, LogTimeModal,
} from '@/features/weekly-tracker/WeeklyActionTracker';
import { WeeklyOneInfoTooltip } from '@/components/ui/WeeklyOneInfoTooltip';
import { MENTORS } from '@/features/landing/mentors.data';

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
  gold:      '#A07F4E',
  goldMid:   '#C09D6C',
  goldLight: '#D4B98C',
  border:    '#E5DED2',
  deep:      '#201917',
  charcoal:  '#313131',
  muted:     '#888070',
  mutedIcon: '#A09684',
  success:   '#339966',
  creamBg:   '#F1ECE5',
  creamCard: '#FBF9F6',
} as const;

// ─── Shared style helpers ─────────────────────────────────────────────
const goldCircleCheck = (size = 32) => ({
  width: size, height: size, borderRadius: '50%',
  background: `linear-gradient(135deg,${C.gold},${C.goldLight})`,
  color: '#fff', display: 'inline-flex', alignItems: 'center' as const, justifyContent: 'center' as const,
  boxShadow: '0 2px 4px rgba(160,127,78,0.20)', flexShrink: 0 as const,
});

const italicNumeral = (n: string, faded = false) => ({
  fontFamily: 'Playfair Display, serif', fontSize: 26, fontStyle: 'italic' as const,
  color: C.gold, lineHeight: 1, textAlign: 'center' as const, width: 32, flexShrink: 0 as const,
  opacity: faded ? 0.45 : 1,
});

function habitIcon(h: Habit) {
  if (h.unit === 'minutes' || h.unit === 'hours') return Clock;
  if (h.kpi_type === 'deals_analyzed') return Activity;
  if (h.kpi_type === 'offers_made') return Target;
  return Users;
}

function fmtCount(h: Habit, count: number) {
  switch (h.unit) {
    case 'minutes':  return formatMinutes(count);
    case 'hours':    return formatHours(count);
    case 'currency': return formatCurrency(count);
    case 'yes_no':   return count >= 1 ? 'Yes' : 'No';
    default:         return `${count}`;
  }
}
function fmtTarget(h: Habit) {
  switch (h.unit) {
    case 'minutes':  return formatMinutes(h.target_per_week);
    case 'hours':    return formatHours(h.target_per_week);
    case 'currency': return formatCurrency(h.target_per_week);
    case 'yes_no':   return '1';
    default:         return `${h.target_per_week}`;
  }
}

// ─── Chips ────────────────────────────────────────────────────────────
function DoneChip() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999,
      background: '#ECFDF5', color: C.success, border: '1px solid #A7F3D0',
      letterSpacing: '.05em', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap' as const,
    }}>
      <Check style={{ width: 10, height: 10 }} /> Done
    </span>
  );
}

function WeeklyOnePill() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 9999,
      background: `linear-gradient(135deg,${C.gold} 0%,${C.goldMid} 60%,${C.goldLight} 100%)`,
      color: '#fff', fontFamily: 'Inter, sans-serif',
      fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' as const,
      boxShadow: '0 2px 4px rgba(160,127,78,0.18)', flexShrink: 0 as const,
    }}>
      <Target style={{ width: 10, height: 10 }} />
      Weekly One
    </span>
  );
}

// ─── Weekly One Stepper Row ───────────────────────────────────────────
interface WeeklyOneRowProps {
  cleared: boolean;
  notSet: boolean;
  commitmentText: string | null;
  isPastWeek: boolean;
  pastWeekCommitment: WeeklyCommitment | null;
  onSaveInline: (text: string) => Promise<void>;
  onMarkComplete: () => void;
  onMarkIncomplete: () => void;
  onOpenModal: () => void;
  onEdit: () => void;
}

function WeeklyOneRow({
  cleared, notSet, commitmentText, isPastWeek, pastWeekCommitment,
  onSaveInline, onMarkComplete, onMarkIncomplete, onOpenModal, onEdit,
}: WeeklyOneRowProps) {
  const [inlineText, setInlineText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSet = async () => {
    const trimmed = inlineText.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSaveInline(trimmed);
      setInlineText('');
    } finally {
      setSaving(false);
    }
  };

  // For past weeks, derive display state from pastWeekCommitment
  const pastDone = isPastWeek && pastWeekCommitment?.status === 'completed';
  const pastHasCommitment = isPastWeek && !!pastWeekCommitment?.commitment_text;
  const effectiveNotSet = isPastWeek ? false : notSet;

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 1fr auto',
    alignItems: effectiveNotSet ? 'flex-start' : 'center',
    gap: 18,
    padding: effectiveNotSet ? '22px 28px 22px 24px' : '18px 28px 18px 24px',
    background: 'rgba(192,157,108,0.10)',
    borderLeft: `3px solid ${C.goldMid}`,
    borderBottom: `1px solid ${C.creamBg}`,
  };

  // Past-week view: just show historical commitment
  if (isPastWeek) {
    return (
      <div style={rowStyle}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {pastDone
            ? <span style={goldCircleCheck(32)}><Check style={{ width: 15, height: 15 }} /></span>
            : <span style={italicNumeral('01', !pastHasCommitment)}>01</span>
          }
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' as const }}>
            <WeeklyOnePill />
          </div>
          {pastHasCommitment ? (
            <div style={{
              fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, letterSpacing: '-.003em',
              color: pastDone ? C.muted : C.deep,
              textDecoration: pastDone ? 'line-through' : 'none',
              textDecorationColor: 'rgba(136,128,112,0.35)',
            }}>
              {pastWeekCommitment!.commitment_text}
            </div>
          ) : (
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
              No Weekly One was set for this week.
            </div>
          )}
        </div>
        <div>
          {pastHasCommitment && (
            pastDone
              ? (
                <button disabled style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8,
                  border: `1px solid ${C.success}`, background: 'rgba(51,153,102,0.10)',
                  color: C.success, fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                  cursor: 'default', whiteSpace: 'nowrap' as const,
                }}>
                  <Check style={{ width: 13, height: 13 }} /> Done
                </button>
              )
              : (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 11, padding: '4px 10px', borderRadius: 9999,
                  background: C.creamBg, color: C.muted, border: `1px solid ${C.border}`,
                  fontFamily: 'Inter, sans-serif', fontWeight: 600,
                }}>
                  Incomplete
                </span>
              )
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={rowStyle}>
      {/* Left marker */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: notSet ? 4 : 0 }}>
        {cleared
          ? <span style={goldCircleCheck(32)}><Check style={{ width: 15, height: 15 }} /></span>
          : <span style={italicNumeral('01', notSet)}>01</span>
        }
      </div>

      {/* Middle content */}
      <div style={{ minWidth: 0 }}>
        {/* Badge row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: notSet ? 10 : 5, flexWrap: 'wrap' as const,
        }}>
          <WeeklyOnePill />
          {notSet && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 9, padding: '1px 7px', borderRadius: 9999,
              background: C.creamCard, color: C.gold,
              border: `1px solid rgba(192,157,108,0.30)`,
              fontFamily: 'Inter, sans-serif', fontWeight: 600,
            }}>
              Not Set Yet
            </span>
          )}
          <WeeklyOneInfoTooltip iconClassName="" style={{ color: 'rgba(160,127,78,0.55)' }} />
        </div>

        {notSet ? (
          <>
            <div style={{
              fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 500,
              color: C.deep, marginBottom: 12, lineHeight: 1.3, letterSpacing: '-.005em',
            }}>
              What's the <em style={{ fontStyle: 'italic', color: C.gold }}>one thing</em> that will move your deal forward this week?
            </div>
            <div style={{ display: 'flex', gap: 8, maxWidth: 560 }}>
              <input
                type="text"
                value={inlineText}
                onChange={e => setInlineText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSet()}
                placeholder="e.g., Call realtor about the duplex on Pearl St."
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: '#fff',
                  fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.deep, outline: 'none',
                }}
              />
              <button
                onClick={handleSet}
                disabled={!inlineText.trim() || saving}
                style={{
                  padding: '0 18px', borderRadius: 8, border: 'none',
                  background: inlineText.trim()
                    ? `linear-gradient(135deg,${C.gold} 0%,${C.goldMid} 60%,${C.goldLight} 100%)`
                    : C.creamBg,
                  color: inlineText.trim() ? '#fff' : C.muted,
                  fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                  cursor: inlineText.trim() ? 'pointer' : 'default',
                  boxShadow: inlineText.trim() ? '0 4px 10px rgba(160,127,78,0.22)' : 'none',
                  whiteSpace: 'nowrap' as const, transition: 'all 150ms',
                }}
              >
                {saving ? 'Saving...' : 'Set It'}
              </button>
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: C.muted, marginTop: 8 }}>
              Need an idea?{' '}
              <span
                style={{ color: C.gold, fontWeight: 600, cursor: 'pointer' }}
                onClick={onOpenModal}
              >
                Browse Weekly One examples →
              </span>
            </div>
          </>
        ) : cleared ? (
          <>
            <div style={{
              fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600,
              color: C.muted, textDecoration: 'line-through',
              textDecorationColor: 'rgba(136,128,112,0.35)', letterSpacing: '-.003em',
            }}>
              {commitmentText}
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.muted, marginTop: 3 }}>
              Complete ·{' '}
              <span
                style={{ color: C.gold, fontWeight: 600, cursor: 'pointer' }}
                onClick={onOpenModal}
              >
                Set next week's →
              </span>
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600,
              color: C.deep, letterSpacing: '-.003em',
            }}>
              {commitmentText}
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.muted, marginTop: 3 }}>
              Set this week ·{' '}
              <span
                style={{ color: C.gold, fontWeight: 600, cursor: 'pointer' }}
                onClick={onEdit}
              >
                Edit
              </span>
            </div>
          </>
        )}
      </div>

      {/* Right affordance */}
      <div style={{ paddingTop: notSet ? 6 : 0 }}>
        {!notSet && (
          cleared
            ? (
              <button
                onClick={onMarkIncomplete}
                title="Click to mark as incomplete"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8,
                  border: `1px solid ${C.success}`, background: 'rgba(51,153,102,0.10)',
                  color: C.success, fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap' as const,
                  transition: 'all 150ms',
                }}
              >
                <Check style={{ width: 13, height: 13 }} /> Done
              </button>
            )
            : (
              <button
                onClick={onMarkComplete}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8,
                  border: `1px solid ${C.goldMid}`, background: '#fff',
                  color: C.gold, fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap' as const,
                }}
              >
                <Check style={{ width: 13, height: 13 }} /> Mark Done
              </button>
            )
        )}
      </div>
    </div>
  );
}

// ─── Habit Stepper ────────────────────────────────────────────────────
function HabitStepper({
  habit, count, isCurrentWeek, onIncrement, onDecrement, onAddMinutes,
}: {
  habit: Habit; count: number; isCurrentWeek: boolean;
  onIncrement: () => void; onDecrement: () => void; onAddMinutes: (m: number) => void;
}) {
  const isTime  = habit.unit === 'minutes' || habit.unit === 'hours';
  const isYesNo = habit.unit === 'yes_no';
  const done    = count >= habit.target_per_week;

  const stepperWrap: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center',
    background: C.creamCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: 2,
  };
  const stepBtn: React.CSSProperties = {
    width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', background: 'transparent', color: '#4A423B', cursor: 'pointer', borderRadius: 5,
  };
  const valWrap: React.CSSProperties = {
    display: 'flex', alignItems: 'baseline', gap: 2,
    padding: '0 8px', minWidth: 56, justifyContent: 'center',
  };

  if (!isCurrentWeek) {
    return (
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: C.muted }}>
        {fmtCount(habit, count)}<span style={{ fontSize: 10 }}>/{fmtTarget(habit)}</span>
      </span>
    );
  }

  if (isYesNo) {
    return (
      <button
        onClick={() => done ? onDecrement() : onIncrement()}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', borderRadius: 7, border: `1px solid ${done ? C.success : C.border}`,
          background: done ? 'rgba(51,153,102,0.10)' : C.creamCard,
          color: done ? C.success : C.muted,
          fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Check style={{ width: 10, height: 10 }} /> {done ? 'Done' : 'Mark done'}
      </button>
    );
  }

  return (
    <div style={stepperWrap}>
      <button
        style={{ ...stepBtn, opacity: (isTime ? count < 15 : count === 0) ? 0.3 : 1 }}
        disabled={isTime ? count < 15 : count === 0}
        onClick={() => isTime ? onAddMinutes(-15) : onDecrement()}
      >
        <Minus style={{ width: 11, height: 11 }} />
      </button>
      <div style={valWrap}>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700, color: C.deep }}>
          {fmtCount(habit, count)}
        </span>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.muted }}>
          /{fmtTarget(habit)}
        </span>
      </div>
      <button style={{ ...stepBtn, color: C.gold }} onClick={() => isTime ? onAddMinutes(15) : onIncrement()}>
        <Plus style={{ width: 11, height: 11 }} />
      </button>
    </div>
  );
}

// ─── Habit Row in Playbook ────────────────────────────────────────────
function HabitPlaybookRow({
  habit, stepNum, count, isCurrentWeek, onIncrement, onDecrement, onAddMinutes, isLast,
}: {
  habit: Habit; stepNum: number; count: number; isCurrentWeek: boolean;
  onIncrement: () => void; onDecrement: () => void; onAddMinutes: (m: number) => void;
  isLast: boolean;
}) {
  const done = count >= habit.target_per_week;
  const HIcon = habitIcon(habit);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '44px 1fr auto', alignItems: 'center',
      gap: 18, padding: '18px 28px 18px 24px',
      background: done ? 'rgba(236,253,245,0.35)' : '#fff',
      borderBottom: isLast ? 'none' : `1px solid ${C.creamBg}`,
    }}>
      {/* Left: numeral or check */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {done
          ? <span style={goldCircleCheck(32)}><Check style={{ width: 15, height: 15 }} /></span>
          : <span style={italicNumeral(String(stepNum).padStart(2, '0'))}>
              {String(stepNum).padStart(2, '0')}
            </span>
        }
      </div>

      {/* Middle */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, color: C.mutedIcon }}>
          <HIcon style={{ width: 11, height: 11, flexShrink: 0 }} />
          <span style={{
            fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
            letterSpacing: '.14em', textTransform: 'uppercase' as const,
          }}>
            {habit.title}
          </span>
        </div>
        <div style={{
          fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600,
          color: done ? C.muted : C.deep,
          textDecoration: done ? 'line-through' : 'none',
          textDecorationColor: 'rgba(136,128,112,0.35)', letterSpacing: '-.003em', marginBottom: 2,
        }}>
          {buildDynamicTitle(habit)}
        </div>
        {habit.description && habit.description !== habit.title && (
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: done ? C.muted : '#4A423B' }}>
            {habit.description}
          </div>
        )}
      </div>

      {/* Right: stepper (always shown for current week so done can be undone) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!isCurrentWeek && done
          ? <DoneChip />
          : (
            <HabitStepper
              habit={habit} count={count} isCurrentWeek={isCurrentWeek}
              onIncrement={onIncrement} onDecrement={onDecrement} onAddMinutes={onAddMinutes}
            />
          )
        }
      </div>
    </div>
  );
}

// ─── Get Unstuck Section ──────────────────────────────────────────────
function GetUnstuckSection({ onMentor, onCalendar }: { onMentor: () => void; onCalendar: () => void }) {
  return (
    <section style={{ marginTop: 40 }}>
      <div style={{
        fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
        letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold, marginBottom: 6,
      }}>
        Hit a wall this week?
      </div>
      <h2 style={{
        fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600,
        color: C.deep, margin: '0 0 16px', letterSpacing: '-.003em', lineHeight: 1.25,
      }}>
        Get <span style={{ color: C.gold }}>Unstuck</span>
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="grid-cols-1 sm:grid-cols-2">

        {/* 1:1 Mentor Call */}
        <div
          onClick={onMentor}
          style={{
            display: 'flex', flexDirection: 'column' as const, padding: '20px 22px',
            background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14,
            cursor: 'pointer',
          }}
        >
          <div style={{
            fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
            letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold, marginBottom: 10,
          }}>1:1 Mentor Call</div>
          <h3 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600,
            color: C.deep, margin: '0 0 8px', letterSpacing: '-.003em', lineHeight: 1.25,
          }}>
            Book time with a <span style={{ color: C.gold }}>vetted operator</span>
          </h3>
          <p style={{
            fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#4A423B',
            margin: 0, lineHeight: 1.55, flex: 1,
          }}>
            30 minutes on a deal, financing question, or this week's One Thing.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {MENTORS.slice(0, 4).map((m, i) => (
                <span key={m.name} style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: `linear-gradient(135deg,${C.gold},${C.goldLight})`,
                  color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #fff', marginLeft: i === 0 ? 0 : -8,
                  boxShadow: '0 1px 2px rgba(160,127,78,0.20)',
                }}>{m.initials}</span>
              ))}
              <span style={{ marginLeft: 8, fontFamily: 'Inter, sans-serif', fontSize: 11, color: C.muted }}>{MENTORS.length} mentors</span>
            </div>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: C.gold }}>
              Browse mentors →
            </span>
          </div>
        </div>

        {/* Group Call */}
        <div
          onClick={onCalendar}
          style={{
            display: 'flex', flexDirection: 'column' as const, padding: '20px 22px',
            background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14,
            cursor: 'pointer',
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <div style={{
              fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
              letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold,
            }}>Group Call</div>
          </div>
          <h3 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600,
            color: C.deep, margin: '0 0 8px', letterSpacing: '-.003em', lineHeight: 1.25,
          }}>
            Bottleneck <span style={{ color: C.gold }}>Breakthrough</span>
          </h3>
          <p style={{
            fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#4A423B',
            margin: 0, lineHeight: 1.55, flex: 1,
          }}>
            Bring your one stuck thing. The room cracks it open in 60 minutes.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 16 }}>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: C.gold }}>
              View calendar and attend call →
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Milestone Card ───────────────────────────────────────────────────
function MilestoneCard({ onUnderContract }: { onUnderContract: () => void }) {
  return (
    <section style={{ marginTop: 28, marginBottom: 40 }}>
      <div style={{
        padding: '22px 28px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(192,157,108,0.14) 0%, rgba(212,185,140,0.06) 100%)',
        border: '1px solid rgba(192,157,108,0.32)',
        display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' as const,
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.gold, display: 'inline-block' }} />
            <span style={{
              fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
              letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold,
            }}>Phase 4 Milestone</span>
          </div>
          <h2 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600,
            margin: '0 0 6px', letterSpacing: '-.003em', lineHeight: 1.25, color: C.deep,
          }}>
            Got a deal <span style={{ color: C.gold }}>under contract?</span>
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#4A423B', margin: 0, lineHeight: 1.5 }}>
            Tap this when you sign a purchase agreement. It moves you to{' '}
            <strong style={{ color: C.deep }}>Phase 5: Closing</strong>.
          </p>
        </div>
        <button
          onClick={onUnderContract}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '14px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg,${C.gold} 0%,${C.goldMid} 60%,${C.goldLight} 100%)`,
            color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600,
            boxShadow: '0 6px 16px rgba(160,127,78,0.30)', flexShrink: 0,
          }}
        >
          <Check style={{ width: 14, height: 14 }} />
          I'm Under Contract
        </button>
      </div>
    </section>
  );
}

// ─── Phase 4 ─────────────────────────────────────────────────────────
const Phase4 = () => {
  const { updateUser, session } = useAuth();
  const {
    currentCommitment, cardState, openSetModal, openDoneModal,
    streakCount: weekly1Streak, saveCommitment, markComplete, markIncomplete,
  } = useWeeklyOne();
  const navigate = useNavigate();

  const {
    habits, loading,
    currentWeek, selectedWeek, setSelectedWeek, isCurrentWeek, availableWeeks,
    weekStartDay,
    getCountForWeek, increment, decrement, logMinutes, addHabit,
    updateHabit, reorderHabitsToOrder, removeHabit, resetToDefaults, updateWeekStartDay,
  } = useHabitTracker();

  const [showSettings,       setShowSettings]       = useState(false);
  const [logTimeHabitId,     setLogTimeHabitId]     = useState<string | null>(null);
  const [pastWeekCommitment, setPastWeekCommitment] = useState<WeeklyCommitment | null>(null);

  // Fetch the Weekly One commitment for the selected past week
  useEffect(() => {
    if (isCurrentWeek) { setPastWeekCommitment(null); return; }
    const userId = session?.user?.id;
    if (!userId) return;
    supabase
      .from('weekly_commitments')
      .select('*')
      .eq('user_id', userId)
      .eq('week_start', selectedWeek)
      .maybeSingle()
      .then(({ data }) => setPastWeekCommitment(data as WeeklyCommitment | null));
  }, [selectedWeek, isCurrentWeek, session?.user?.id]);

  // Weekly One state
  const weeklyOneCleared = cardState === 'completed';
  const weeklyOneNotSet  = !currentCommitment?.commitment_text;

  // Progress
  const habitsComplete = useMemo(
    () => habits.filter(h => getCountForWeek(h.id, currentWeek) >= h.target_per_week).length,
    [habits, getCountForWeek, currentWeek],
  );
  const totalSteps     = 1 + habits.length;
  const completedSteps = (weeklyOneCleared ? 1 : 0) + habitsComplete;
  const progressPct    = totalSteps > 0 ? Math.round(completedSteps / totalSteps * 100) : 0;
  const stepsLeft      = totalSteps - completedSteps;

  // Week label
  const weekEndForSelected = useMemo(() => getWeekEnd(selectedWeek), [selectedWeek]);
  const weekLabel = selectedWeek
    ? `${format(new Date(selectedWeek + 'T00:00:00'), 'MMM d')}–${format(weekEndForSelected, 'MMM d')}`
    : '';
  const nextMonday = useMemo(() => {
    const end = getWeekEnd(currentWeek);
    return format(addDays(end, 1), 'MMM d');
  }, [currentWeek]);

  const logTimeHabit = logTimeHabitId ? habits.find(h => h.id === logTimeHabitId) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <PhaseIntroCard
        phase={4}
        title="Action"
        icon={TrendingUp}
        description="This is where deals actually happen. The best way to learn is through action, so it's time to get in the reps: analyze deals, submit offers, and follow up. You won't always feel ready, but take action anyway. Lean on your mentors when you get stuck. You become the results of what you repeatedly do. This is what separates real investors from wannabes."
      />

      {/* ── Unified Playbook Card ── */}
      <div style={{
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14,
        overflow: 'hidden', marginBottom: 0,
      }}>
        {/* Header */}
        <div style={{ padding: '22px 28px 18px', borderBottom: `1px solid ${C.creamBg}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            {/* Left: label + title */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
                letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold, marginBottom: 6,
              }}>
                Your Playbook
              </div>
              <h2 style={{
                fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600,
                color: C.deep, margin: 0, letterSpacing: '-.003em', lineHeight: 1.25,
              }}>
                This Week's <em style={{ fontStyle: 'italic', color: C.gold }}>Action Plan.</em>
              </h2>
            </div>

            {/* Right: week nav + three-dot settings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Week navigation */}
              <button
                onClick={() => {
                  const idx = availableWeeks.indexOf(selectedWeek);
                  if (idx < availableWeeks.length - 1) setSelectedWeek(availableWeeks[idx + 1]);
                }}
                disabled={availableWeeks.indexOf(selectedWeek) >= availableWeeks.length - 1}
                style={{
                  width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${C.border}`, background: C.creamCard, color: C.muted,
                  cursor: availableWeeks.indexOf(selectedWeek) >= availableWeeks.length - 1 ? 'not-allowed' : 'pointer',
                  borderRadius: 7, opacity: availableWeeks.indexOf(selectedWeek) >= availableWeeks.length - 1 ? 0.3 : 1,
                }}
              >
                <ChevronLeft style={{ width: 12, height: 12 }} />
              </button>
              <div style={{
                padding: '5px 14px', borderRadius: 7,
                border: `1px solid ${C.border}`, background: C.creamCard,
                fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: C.deep,
                minWidth: 110, textAlign: 'center' as const,
              }}>
                {weekLabel}
              </div>
              <button
                onClick={() => {
                  const idx = availableWeeks.indexOf(selectedWeek);
                  if (idx > 0) setSelectedWeek(availableWeeks[idx - 1]);
                }}
                disabled={isCurrentWeek}
                style={{
                  width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${C.border}`, background: C.creamCard, color: C.muted,
                  cursor: isCurrentWeek ? 'not-allowed' : 'pointer',
                  borderRadius: 7, opacity: isCurrentWeek ? 0.3 : 1,
                }}
              >
                <ChevronRight style={{ width: 12, height: 12 }} />
              </button>

              {/* Three-dot settings toggle */}
              <button
                onClick={() => setShowSettings(s => !s)}
                title={showSettings ? 'Close settings' : 'Settings'}
                style={{
                  width: 28, height: 28, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: 7,
                  border: `1px solid ${C.border}`, background: showSettings ? C.creamBg : C.creamCard,
                  color: showSettings ? C.gold : C.muted, cursor: 'pointer',
                }}
              >
                <MoreHorizontal style={{ width: 14, height: 14 }} />
              </button>
            </div>
          </div>
        </div>

        {/* Settings panel */}
        <AnimatePresence>
          {showSettings && (
            <SettingsPanel
              habits={habits}
              weekStartDay={weekStartDay}
              onUpdateWeekStartDay={updateWeekStartDay}
              onReorderFull={reorderHabitsToOrder}
              onUpdateHabit={updateHabit}
              onAddHabit={addHabit}
              onRemoveHabit={removeHabit}
              onReset={resetToDefaults}
              onClose={() => setShowSettings(false)}
            />
          )}
        </AnimatePresence>

        {/* Past-week overlay notice */}
        {!isCurrentWeek && (
          <div style={{
            padding: '10px 28px', background: 'rgba(192,157,108,0.06)',
            borderBottom: `1px solid ${C.creamBg}`,
            fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.muted,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              fontFamily: 'Inter, sans-serif', fontSize: 9, fontWeight: 700,
              letterSpacing: '.18em', textTransform: 'uppercase' as const, color: C.gold,
              background: 'rgba(192,157,108,0.12)', padding: '2px 6px', borderRadius: 4,
            }}>PAST</span>
            Viewing a past week — snapshot only
          </div>
        )}

        {/* Step 01: Weekly One */}
        <WeeklyOneRow
          cleared={weeklyOneCleared}
          notSet={weeklyOneNotSet}
          commitmentText={currentCommitment?.commitment_text ?? null}
          isPastWeek={!isCurrentWeek}
          pastWeekCommitment={pastWeekCommitment}
          onSaveInline={async (text) => { await saveCommitment(text, 0, ''); }}
          onMarkComplete={markComplete}
          onMarkIncomplete={markIncomplete}
          onOpenModal={openSetModal}
          onEdit={openSetModal}
        />

        {/* Steps 02+: Habits */}
        {habits.length === 0 ? (
          <div style={{ padding: '24px 28px', textAlign: 'center' as const }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: C.muted, marginBottom: 12 }}>
              No habits set up yet.
            </p>
            <button
              onClick={resetToDefaults}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg,${C.gold},${C.goldMid},${C.goldLight})`,
                color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                boxShadow: '0 4px 10px rgba(160,127,78,0.22)',
              }}
            >
              Set Up My Habits
            </button>
          </div>
        ) : (
          habits.map((habit, i) => (
            <HabitPlaybookRow
              key={habit.id}
              habit={habit}
              stepNum={i + 2}
              count={getCountForWeek(habit.id, selectedWeek)}
              isCurrentWeek={isCurrentWeek}
              onIncrement={() => increment(habit.id)}
              onDecrement={() => decrement(habit.id)}
              onAddMinutes={mins => logMinutes(habit.id, mins)}
              isLast={i === habits.length - 1}
            />
          ))
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 28px', borderTop: `1px solid ${C.creamBg}`, background: C.creamCard,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.muted, flexWrap: 'wrap' as const, gap: 6,
        }}>
          <span>
            <span style={{ color: C.gold, fontWeight: 600 }}>{progressPct}% complete</span>
            {' · '}{completedSteps} of {totalSteps} steps done · {stepsLeft} left this week
          </span>
          <span
            style={{ color: C.gold, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => navigate('/action-tracker')}
          >
            View Action Tracker →
          </span>
        </div>

        {/* Bottom progress bar */}
        <div style={{ width: '100%', height: 4, background: C.creamBg }}>
          <div style={{
            height: '100%',
            background: `linear-gradient(90deg,${C.gold},${C.goldMid},${C.goldLight})`,
            width: `${progressPct}%`, transition: 'width 300ms cubic-bezier(0.23,1,0.32,1)',
          }} />
        </div>
      </div>

      {/* Log time modal */}
      {logTimeHabit && (
        <LogTimeModal
          habit={logTimeHabit}
          currentCount={getCountForWeek(logTimeHabit.id, currentWeek)}
          onLog={mins => logMinutes(logTimeHabit.id, mins)}
          onClose={() => setLogTimeHabitId(null)}
        />
      )}

      {/* ── Get Unstuck ── */}
      <GetUnstuckSection
        onMentor={() => navigate('/mentors')}
        onCalendar={() => navigate('/calendar')}
      />

      {/* ── Milestone ── */}
      <MilestoneCard onUnderContract={() => updateUser({ phase: 5 })} />
    </div>
  );
};

export default Phase4;
